import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ScheduledTask } from '../../services/maintenance.api';
import { MaintenanceTaskCard } from './MaintenanceTaskCard';

interface Props {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  headerBg: string;
  count: number;
  tasks: ScheduledTask[];
  collapsed?: boolean;
  onToggle?: () => void;
  onDone: (taskId: string, performedAt: string) => Promise<void>;
  onSkip: (taskId: string) => void;
  onSnooze: (taskId: string, snoozedUntil: string) => Promise<void>;
  onDIY: (task: ScheduledTask) => void;
  onOrderParts: (task: ScheduledTask) => void;
}

export function MaintenanceSection({
  title,
  icon: Icon,
  iconColor,
  count,
  tasks,
  collapsed = false,
  onToggle,
  onDone,
  onSkip,
  onSnooze,
  onDIY,
  onOrderParts,
}: Readonly<Props>) {
  if (count === 0) return null;

  return (
    <div className="mb-5">
      {/* Section header */}
      <button
        onClick={onToggle}
        className={cn(
          'w-full flex items-center justify-between px-0 py-2 mb-2',
          onToggle ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        <div className="flex items-center gap-2">
          <Icon className={cn('h-3.5 w-3.5', iconColor)} strokeWidth={2.5} />
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {title}
          </span>
          <span className="inline-flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-xs font-semibold text-zinc-600 dark:text-zinc-300 min-w-[20px]">
            {count}
          </span>
        </div>
        {onToggle && (
          <div className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">
            <span className="text-[11px]">{collapsed ? 'Show' : 'Hide'}</span>
            {collapsed
              ? <ChevronDown className="h-3.5 w-3.5" />
              : <ChevronUp className="h-3.5 w-3.5" />
            }
          </div>
        )}
      </button>

      {/* Divider */}
      <div className="border-t border-zinc-100 dark:border-zinc-800 mb-2" />

      {/* Task cards */}
      {!collapsed && (
        <div className="space-y-2">
          {tasks.map((task) => (
            <MaintenanceTaskCard
              key={task.id}
              task={task}
              onDone={onDone}
              onSkip={onSkip}
              onSnooze={onSnooze}
              onDIY={onDIY}
              onOrderParts={onOrderParts}
            />
          ))}
        </div>
      )}
    </div>
  );
}

